import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import { useSite } from '../contexts/SiteContext';
import * as FiIcons from 'react-icons/fi';

const { FiFacebook, FiInstagram, FiYoutube, FiMail, FiPhone, FiMapPin } = FiIcons;

const Footer = () => {
  const { siteConfig } = useSite();
  const currentYear = new Date().getFullYear();
  const hasValidEmail = Boolean(siteConfig.email && siteConfig.email !== '_');
  const phones = (siteConfig.phone || '')
    .split(/\s*[,;|]\s*/)
    .map((phone) => phone.trim())
    .filter(Boolean);

  return (
    <footer className="bg-secondary-800 text-white" style={{ backgroundColor: siteConfig.secondary_color }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <ImageWithFallback
                src={siteConfig.logo_url}
                alt={siteConfig.site_name}
                className="w-12 h-12 rounded-full object-contain bg-white"
                fallback={
                  <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xl">ΑΟΧ</span>
                  </div>
                }
              />
              <div>
                <h3 className="text-xl font-bold">{siteConfig.site_name}</h3>
                <p className="text-gray-300">{siteConfig.site_description}</p>
              </div>
            </div>
            <p className="text-gray-300 mb-4">
              {siteConfig.footer_about_text || 'Από το 1945, ο Α.Ο. Χανιά συνεχίζει την παράδοση στο ποδόσφαιρο. Η γυναικεία μας ομάδα αγωνίζεται στη Γ\' Εθνική Κατηγορία με στόχο την προώθηση του γυναικείου ποδοσφαίρου στην Κρήτη.'}
            </p>
            <div className="flex space-x-4">
              {siteConfig.social_facebook && (
                <a href={siteConfig.social_facebook} className="text-gray-300 hover:text-primary-400 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <SafeIcon icon={FiFacebook} className="w-6 h-6" />
                </a>
              )}
              {siteConfig.social_instagram && (
                <a href={siteConfig.social_instagram} className="text-gray-300 hover:text-primary-400 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <SafeIcon icon={FiInstagram} className="w-6 h-6" />
                </a>
              )}
              {siteConfig.social_youtube && (
                <a href={siteConfig.social_youtube} className="text-gray-300 hover:text-red-400 transition-colors" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <SafeIcon icon={FiYoutube} className="w-6 h-6" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Γρήγοροι Σύνδεσμοι</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">Αρχική</Link></li>
              <li><Link to="/team" className="text-gray-300 hover:text-white transition-colors">Η Ομάδα</Link></li>
              <li><Link to="/matches" className="text-gray-300 hover:text-white transition-colors">Αγώνες</Link></li>
              <li><Link to="/academy" className="text-gray-300 hover:text-white transition-colors">Ακαδημία</Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors">Νέα</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Επικοινωνία</h3>
            <div className="space-y-3">
              {siteConfig.address && (
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 text-primary-400 mt-0.5 shrink-0" />
                  <span className="text-gray-300 text-sm">{siteConfig.address}</span>
                </div>
              )}
              {phones.length > 0 && (
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 text-primary-400 mt-0.5 shrink-0" />
                  <div className="flex flex-col">
                    {phones.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                        className="text-gray-300 text-sm hover:text-white transition-colors"
                      >
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {hasValidEmail && (
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4 text-primary-400 shrink-0" />
                  <a href={`mailto:${siteConfig.email}`} className="text-gray-300 text-sm hover:text-white transition-colors break-all">{siteConfig.email}</a>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-300 text-sm">
            © {currentYear} {siteConfig.site_name} - {siteConfig.site_description}. Όλα τα δικαιώματα διατηρούνται.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
